import { useTranslation } from 'react-i18next';

import { type Board, useBoardDetail } from '@/entities/board';
import { useParticipants } from '@/entities/collab';
import { usePresence } from '@/entities/realtime';
import { useViewer } from '@/entities/viewer';
import { Avatar, AvatarStack, IconButton, Icons, Popover } from '@/shared/ui';

import styles from './BoardAccess.module.css';

interface Props {
  board: Board;
  /** Opens the access settings dialog (owned by the page). */
  onOpenAccess: () => void;
}

/**
 * Board header cluster: the people PRESENT on this board right now (up to three avatars, +N for
 * the rest; one person with several tabs counts once; nothing at all when nobody else is here),
 * and - separately - the access-settings button, which opens the full authorized membership
 * including people who are offline. Presence is ephemeral and never means access.
 */
export function ParticipantsCluster({ board, onOpenAccess }: Props) {
  const { t } = useTranslation();
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const participants = useParticipants(board.id);
  const detail = useBoardDetail(board.id);
  const presence = usePresence(board.id);
  const names = new Map((participants.data ?? []).map((p) => [p.user_id, p.display_name]));
  const titles = new Map((detail.data?.cards ?? []).map((c) => [c.id, c.title]));
  // Present people other than the viewer, each once, in a stable order.
  const present = [...new Set(presence.online)]
    .filter((id) => id !== viewerId)
    .map((id) => ({ id, name: names.get(id) ?? t('access.someone') }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const editingOf = (userId: string): string | null => {
    for (const [cardId, users] of Object.entries(presence.editing)) {
      if (users.includes(userId)) return titles.get(cardId) ?? t('access.aCard');
    }
    return null;
  };
  return (
    <div className={styles.cluster} role="group" aria-label={t('access.cluster')}>
      {present.length > 0 && (
        <Popover
          label={t('access.presentNow')}
          width={320}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              className={styles.stackButton}
              aria-label={t('access.presentCount', { count: present.length })}
              data-testid="participants-button"
              {...props}
            >
              <AvatarStack
                people={present.map((p) => ({ id: p.id, name: p.name, online: true }))}
                max={3}
              />
            </button>
          )}
        >
          {(close) => (
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>{t('access.presentNow')}</h3>
              <ul className={styles.people}>
                {present.map((p) => {
                  const editing = editingOf(p.id);
                  return (
                    <li key={p.id} className={styles.person}>
                      <Avatar id={p.id} name={p.name} online />
                      <span className={styles.personText}>
                        <span>{p.name}</span>
                        <span className={styles.sub}>
                          {editing !== null
                            ? t('access.editingCard', { title: editing })
                            : t('access.viewingBoard')}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className={styles.stackButton}
                onClick={() => {
                  close();
                  onOpenAccess();
                }}
              >
                {t('access.settings')}
              </button>
            </div>
          )}
        </Popover>
      )}
      <IconButton
        label={t('access.settings')}
        icon={<Icons.key />}
        data-testid="access-settings-button"
        onClick={onOpenAccess}
      />
    </div>
  );
}
